import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import BreathOrb from "../breathwork/BreathOrb";
import { ChevronLeft, X, ChevronRight, CheckCircle2, Leaf, Flower2, Hand, Cloud, RefreshCw, BookOpen, PersonStanding, Bed, User } from "lucide-react";

const POSTURES = {
  coach: { titleKey: "posture_coach_title", subKey: "posture_coach_sub", icon: BookOpen },
  goalkeeper: { titleKey: "posture_goalkeeper_title", subKey: "posture_goalkeeper_sub", icon: PersonStanding },
  wall: { titleKey: "posture_wall_title", subKey: "posture_wall_sub", icon: Hand },
  lay: { titleKey: "posture_lay_title", subKey: "posture_lay_sub", icon: Bed },
  upright: { titleKey: "posture_upright_title", subKey: "posture_upright_sub", icon: User },
};

const STAGES = [
  { key: "pursed", titleKey: "copd_stage_pursed_title", subKey: "copd_stage_pursed_sub", introTitleKey: "copd_intro_pursed_title", introBodyKey: "copd_intro_pursed_body", stepKeys: ["copd_pursed_step1", "copd_pursed_step2", "copd_pursed_step3"], postureKeys: ["goalkeeper", "wall"], color: "#5FA0AF", icon: Leaf, rounds: 5, phases: [{ key: "inhale", seconds: 3 }, { key: "exhale", seconds: 6 }] },
  { key: "belly", titleKey: "copd_stage_belly_title", subKey: "copd_stage_belly_sub", introTitleKey: "copd_intro_belly_title", introBodyKey: "copd_intro_belly_body", stepKeys: ["copd_belly_step1", "copd_belly_step2", "copd_belly_step3", "copd_belly_step4"], postureKeys: ["coach", "lay"], color: "#8FB3A0", icon: Flower2, rounds: 5, phases: [{ key: "inhale", seconds: 4 }, { key: "exhale", seconds: 6 }] },
  { key: "contact", titleKey: "copd_stage_contact_title", subKey: "copd_stage_contact_sub", introTitleKey: "copd_intro_contact_title", introBodyKey: "copd_intro_contact_body", stepKeys: ["copd_contact_step1", "copd_contact_step2", "copd_contact_step3"], postureKeys: ["coach"], color: "#B196C4", icon: Hand, rounds: 5, phases: [{ key: "inhale", seconds: 4 }, { key: "exhale", seconds: 5 }] },
  { key: "huff", titleKey: "copd_stage_huff_title", subKey: "copd_stage_huff_sub", introTitleKey: "copd_intro_huff_title", introBodyKey: "copd_intro_huff_body", stepKeys: ["copd_huff_step1", "copd_huff_step2", "copd_huff_step3"], postureKeys: ["coach", "upright"], color: "#C99A5F", icon: Cloud, rounds: 4, phases: [{ key: "inhale", seconds: 3 }, { key: "exhale", seconds: 3 }] },
  { key: "acbt", titleKey: "copd_stage_acbt_title", subKey: "copd_stage_acbt_sub", introTitleKey: "copd_intro_acbt_title", introBodyKey: "copd_intro_acbt_body", stepKeys: ["copd_acbt_p1_body", "copd_acbt_p2_body", "copd_acbt_p3_body"], postureKeys: ["coach"], color: "#7A9EBF", icon: RefreshCw, rounds: 3, phases: [{ key: "inhale", seconds: 4 }, { key: "hold_in", seconds: 3 }, { key: "exhale", seconds: 5 }] },
];

function Scale({ value, onChange, prefix, accent }) {
  return (
    <div className="grid grid-cols-10 gap-1.5">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
        const on = value === n;
        return (
          <button key={n} onClick={() => onChange(n)} data-testid={`${prefix}-${n}`}
            className="flex h-10 items-center justify-center rounded-lg border text-sm font-semibold transition-colors"
            style={on ? { backgroundColor: accent, borderColor: accent, color: "#fff" } : { borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}>{n}</button>
        );
      })}
    </div>
  );
}

export default function CopdFlow() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("breathe_easy_btn"));
  const [step, setStep] = useState(0);
  const [stageKey, setStageKey] = useState(null);
  const [postureKey, setPostureKey] = useState(null);
  const [before, setBefore] = useState(null);
  const [now, setNow] = useState(null);
  const [run, setRun] = useState({ completed: false, elapsed_seconds: 0 });
  const stage = STAGES.find((s) => s.key === stageKey) || null;

  const goHome = () => navigate("/app/breathwork");
  const onBack = () => { if (step === 0) navigate("/app/breathwork"); else if (step === 5) goHome(); else setStep(step - 1); };

  const pickStage = (s) => { setStageKey(s.key); setPostureKey(s.postureKeys.length === 1 ? s.postureKeys[0] : null); setStep(1); };

  const submit = () => {
    if (!stage || !before || !now) return;
    api.post("/copd-sessions", { technique_key: stage.key, posture_key: postureKey, difficulty_before: before, difficulty_after: now, duration_seconds: run.elapsed_seconds, completed: run.completed }).catch(() => toast.error(t("something_went_wrong")));
    setStep(5);
  };

  if (step === 3 && stage) {
    return (
      <div className="mx-auto max-w-xl" data-testid="copd-flow-player">
        <BreathOrb phases={stage.phases} rounds={stage.rounds} techniqueLabel={t(stage.titleKey)}
          onComplete={(r) => { setRun(r); setStep(4); }} onCancel={() => setStep(2)} />
      </div>
    );
  }

  const posture = postureKey ? POSTURES[postureKey] : null;

  return (
    <div className="mx-auto max-w-xl space-y-4" data-testid="copd-flow-screen">
      <div className="flex items-center gap-3">
        <button onClick={onBack} data-testid="copd-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent">{step === 0 ? <X className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}</button>
        <h1 className="font-display text-xl text-foreground">{t("breathe_easy_btn")}</h1>
      </div>

      {step === 0 ? (
        <div className="space-y-3">
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("copd_picker_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("copd_picker_sub")}</p>
          </div>
          {STAGES.map((s) => (
            <button key={s.key} onClick={() => pickStage(s)} data-testid={`copd-card-${s.key}`}
              className="flex w-full items-center gap-3 rounded-2xl border bg-card p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md" style={{ borderColor: s.color }}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: s.color + "22" }}><s.icon className="h-5 w-5" style={{ color: s.color }} /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-sm text-foreground">{t(s.titleKey)}</span>
                <span className="block truncate text-xs text-muted-foreground">{t(s.subKey)}</span>
              </span>
              <ChevronRight className="h-5 w-5" style={{ color: s.color }} />
            </button>
          ))}
        </div>
      ) : null}

      {step === 1 && stage ? (
        <div className="space-y-3">
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("posture_recommended_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{stage.postureKeys.length > 1 ? t("posture_recommended_sub") : t(POSTURES[stage.postureKeys[0]].subKey)}</p>
          </div>
          {stage.postureKeys.map((pk) => {
            const p = POSTURES[pk];
            const on = postureKey === pk;
            return (
              <button key={pk} onClick={() => setPostureKey(pk)} data-testid={`copd-posture-${pk}`}
                className="flex w-full items-center gap-3 rounded-2xl border bg-card p-3 text-left transition-colors" style={on ? { borderColor: stage.color, backgroundColor: stage.color + "12", borderWidth: 2 } : { borderColor: "hsl(var(--border))" }}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: stage.color + "22" }}><p.icon className="h-5 w-5" style={{ color: stage.color }} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-display text-sm text-foreground">{t(p.titleKey)}</span>
                  <span className="block text-xs text-muted-foreground">{t(p.subKey)}</span>
                </span>
                {on ? <CheckCircle2 className="h-5 w-5" style={{ color: stage.color }} /> : null}
              </button>
            );
          })}
          <button onClick={() => postureKey && setStep(2)} disabled={!postureKey} data-testid="copd-posture-continue" className="w-full rounded-full py-3 font-semibold text-white disabled:opacity-40" style={{ backgroundColor: stage.color }}>{t("copd_step_next")}</button>
        </div>
      ) : null}

      {step === 2 && stage ? (
        <div className="space-y-4 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: stage.color + "22" }}><stage.icon className="h-8 w-8" style={{ color: stage.color }} /></span>
          <h2 className="font-display text-2xl text-foreground">{t(stage.introTitleKey)}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{t(stage.introBodyKey)}</p>
          <div className="rounded-2xl border border-border bg-secondary/40 p-4 text-left">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: stage.color }}>{t("explanation_how_it_works")}</p>
            <div className="space-y-2">
              {stage.stepKeys.map((k, i) => (
                <div key={k} className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: stage.color }}>{i + 1}</span>
                  <p className="text-sm text-foreground">{t(k)}</p>
                </div>
              ))}
            </div>
            {posture ? (
              <div className="mt-3 flex items-center gap-2 border-t border-border pt-2.5">
                <posture.icon className="h-4 w-4" style={{ color: stage.color }} />
                <span className="text-xs font-medium text-muted-foreground">{t(posture.titleKey)}</span>
              </div>
            ) : null}
          </div>
          <button onClick={() => setStep(3)} data-testid="copd-explanation-ready" className="w-full rounded-full py-3 font-semibold text-white" style={{ backgroundColor: stage.color }}>{t("explanation_ready_btn")}</button>
        </div>
      ) : null}

      {step === 4 && stage ? (
        <div className="space-y-5">
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("copd_intensity_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("copd_intensity_scale_hint")}</p>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground">{t("copd_intensity_before_short")}</p>
            <Scale value={before} onChange={setBefore} prefix="copd-before" accent={stage.color} />
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground">{t("copd_intensity_after_short")}</p>
            <Scale value={now} onChange={setNow} prefix="copd-after" accent={stage.color} />
          </div>
          <button onClick={submit} disabled={!before || !now} data-testid="copd-intensity-continue" className="w-full rounded-full py-3 font-semibold text-white disabled:opacity-40" style={{ backgroundColor: stage.color }}>{t("copd_intensity_continue")}</button>
        </div>
      ) : null}

      {step === 5 && stage ? (
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full" style={{ backgroundColor: stage.color + "22" }}><CheckCircle2 className="h-10 w-10" style={{ color: stage.color }} /></span>
          <h2 className="font-display text-2xl text-foreground">{t("session_success_title")}</h2>
          <p className="max-w-sm text-sm text-muted-foreground">{t("session_success_body")}</p>
          <div className="flex w-full max-w-xs flex-col gap-2">
            <button onClick={goHome} data-testid="copd-success-done" className="w-full rounded-full py-3 font-semibold text-white" style={{ backgroundColor: stage.color }}>{t("session_done_btn")}</button>
            <button onClick={() => navigate("/app/copd/analytics")} data-testid="copd-goto-analytics" className="w-full rounded-full border border-border py-2.5 text-sm font-medium text-foreground hover:bg-accent">{t("copd_analytics_title")}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
